import React from "react";
import {
  Stack,
  Group,
  TextInput,
  Textarea,
  SegmentedControl,
  Text,
} from "@mantine/core";
import StandardizedPhoneInput, {
  ParsedPhoneNumber,
} from "./StandardizedPhoneInput";
import { GiftCardDeliveryChannel } from "../../types/giftCards";

/** Cap mirrors the backend (`validate_gift_message`) so the UI fails fast. */
export const GIFT_CARD_MESSAGE_MAX_LENGTH = 250;

interface GiftCardRecipientFieldsProps {
  channel: GiftCardDeliveryChannel;
  onChannelChange: (channel: GiftCardDeliveryChannel) => void;
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  phoneDisplay: string;
  onPhoneDisplayChange: (value: string) => void;
  parsedPhone: ParsedPhoneNumber | null;
  onParsedPhoneChange: (parsed: ParsedPhoneNumber | null) => void;
  email: string;
  onEmailChange: (value: string) => void;
  message: string;
  onMessageChange: (value: string) => void;
  /** A minimal email check for inline error display (backend re-validates). */
  emailValid: boolean;
  disabled?: boolean;
}

/**
 * The recipient contact + channel + personal-message inputs shared by the
 * gift-card sale modal (capture at sale time) and the post-checkout send-link
 * fork. Fully controlled — the parent owns the state and the channel↔contact
 * "ready to send" validation; this only renders the fields for the chosen
 * channel. Stacks single-column, so it works on mobile as-is.
 */
export default function GiftCardRecipientFields({
  channel,
  onChannelChange,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  phoneDisplay,
  onPhoneDisplayChange,
  parsedPhone,
  onParsedPhoneChange,
  email,
  onEmailChange,
  message,
  onMessageChange,
  emailValid,
  disabled = false,
}: GiftCardRecipientFieldsProps) {
  const wantsSms = channel === "sms" || channel === "both";
  const wantsEmail = channel === "email" || channel === "both";

  return (
    <Stack gap="md">
      <SegmentedControl
        fullWidth
        value={channel}
        onChange={(value) => onChannelChange(value as GiftCardDeliveryChannel)}
        disabled={disabled}
        data={[
          { label: "Text", value: "sms" },
          { label: "Email", value: "email" },
          { label: "Both", value: "both" },
        ]}
      />
      <Group grow>
        <TextInput
          label="Recipient first name"
          placeholder="Optional"
          value={firstName}
          onChange={(e) => onFirstNameChange(e.currentTarget.value)}
          disabled={disabled}
        />
        <TextInput
          label="Recipient last name"
          placeholder="Optional"
          value={lastName}
          onChange={(e) => onLastNameChange(e.currentTarget.value)}
          disabled={disabled}
        />
      </Group>
      {wantsSms && (
        <StandardizedPhoneInput
          label="Recipient mobile number"
          required
          value={phoneDisplay}
          onChange={onPhoneDisplayChange}
          onParsedChange={onParsedPhoneChange}
          disabled={disabled}
          error={
            phoneDisplay && parsedPhone === null
              ? "Enter a complete 10-digit number."
              : undefined
          }
        />
      )}
      {wantsEmail && (
        <TextInput
          label="Recipient email"
          required
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => onEmailChange(e.currentTarget.value)}
          disabled={disabled}
          error={
            email && !emailValid ? "Enter a valid email address." : undefined
          }
        />
      )}
      {/* Character count sits OUTSIDE the input, aligned to the bottom-right. */}
      <Stack gap={4}>
        <Textarea
          label="Personal message"
          placeholder="Add a personal note (optional)"
          value={message}
          onChange={(e) => onMessageChange(e.currentTarget.value)}
          disabled={disabled}
          maxLength={GIFT_CARD_MESSAGE_MAX_LENGTH}
          autosize
          minRows={2}
          maxRows={4}
        />
        <Text size="xs" c="dimmed" ta="right">
          {message.length}/{GIFT_CARD_MESSAGE_MAX_LENGTH}
        </Text>
      </Stack>
    </Stack>
  );
}
